'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Check, Loader2, Square, Trash2 } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import type { Club } from '@/types/tournament.model';
import { getCourtsCountForSport } from '@/utils/club-utils';
import type { KnockoutStart } from '@/utils/tournament-schedule';
import type { CreateScheduleRequest, ScheduleEntry } from '@/types/schedule.model';
import {
  generateSlotsForDuration,
  getDatesInRange,
  buildScheduleEntries,
  SLOT_INTERVAL_OPTIONS,
  resolveKnockoutStart,
  startTimesWithCourts,
  type KnockoutMode,
  type TimeSlotsByDate,
} from './scheduleBlockUtils';
import { BlockNameInput } from './BlockNameInput';
import { ScheduleDatesSelector, type ScheduleDatesMode } from './ScheduleDatesSelector';
import { DayScheduleCard } from './DayScheduleCard';
import { KnockoutStartPanel } from './KnockoutStartPanel';

export interface CreateScheduleBlockFormProps {
  club: Club;
  matchDuration: number;
  /** Filter courts by sport. When provided, max courts = count of courts for this sport. */
  sportUid?: string | null;
  /** Not used when hideSubmit is set. */
  onClose?: () => void;
  /** Parent creates the schedule (API). Not used when hideSubmit is set. */
  onSubmitRequest?: (request: CreateScheduleRequest) => Promise<void>;
  /** Optional: min date for range (e.g. day after signup deadline). */
  minDate?: Date;
  /** Hydrates the initial grid (editing an existing round). */
  initialSlots?: TimeSlotsByDate;
  /** Initial block name (editing an existing round). */
  initialName?: string;
  /** Edits a single week (hides the from/to range selector). */
  singleWeekMode?: boolean;
  /** Submit button label (default: "Crear bloque"). */
  submitLabel?: string;
  /** Submit button label while submitting (default: "Creando..."). */
  submittingLabel?: string;
  /**
   * When set, the grid spans the 7 days starting at this date and the range
   * selector is hidden (used by LONG: pick the first-round start day).
   */
  weekStartDate?: Date;
  /** Notifies the parent of the current grid state as ScheduleEntry[] on every change. */
  onSlotsChange?: (entries: ScheduleEntry[]) => void;
  /** Hides the name input and the internal submit button (parent drives continuation). */
  hideSubmit?: boolean;
  /** Calendar mode. 'single' for AMERICAN tournaments. Default: 'range'. */
  dateSelectionMode?: ScheduleDatesMode;
  /** SHORT only: lets the manager mark the slot from which the knockout bracket is played. */
  allowKnockoutStart?: boolean;
}

export function CreateScheduleBlockForm({
  club,
  matchDuration,
  sportUid,
  onClose,
  onSubmitRequest,
  minDate,
  initialSlots,
  initialName,
  singleWeekMode = false,
  submitLabel = 'Crear bloque',
  submittingLabel = 'Creando...',
  weekStartDate,
  onSlotsChange,
  hideSubmit = false,
  dateSelectionMode = 'range',
  allowKnockoutStart = false,
}: CreateScheduleBlockFormProps) {
  const [name, setName] = useState(initialName ?? '');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [timeSlotsByDate, setTimeSlotsByDate] = useState<TimeSlotsByDate>(initialSlots ?? {});
  /** Per day: startTimes marked with check (changing one updates all). */
  const [checkedSlotsByDate, setCheckedSlotsByDate] = useState<Record<string, string[]>>({});
  /** Per day: enables mass selection mode (shows checkboxes and allows drag to select ranges). */
  const [massSelectionEnabledByDate, setMassSelectionEnabledByDate] = useState<Record<string, true>>({});
  const [copiedSlots, setCopiedSlots] = useState<Record<string, number> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetDayTarget, setResetDayTarget] = useState<string | null>(null);
  const [resetAllOpen, setResetAllOpen] = useState(false);
  /** Interval in minutes between the start of each block (15 or 30). */
  const [slotIntervalMinutes, setSlotIntervalMinutes] = useState(30);
  /** If there are selected time slots and the user changes "Every", we show confirmation and store the new value here. */
  const [pendingIntervalChange, setPendingIntervalChange] = useState<number | null>(null);

  const [knockoutMode, setKnockoutMode] = useState<KnockoutMode>('LAST_DAY');
  const [knockoutSlot, setKnockoutSlot] = useState<KnockoutStart | null>(null);

  const nameError = useMemo(() => {
    const t = name.trim();
    if (t.length === 0) return undefined;
    return t.length < 2 ? 'Mínimo 2 caracteres' : undefined;
  }, [name]);

  const nameValid = name.trim().length >= 2;

  const availableSlots = useMemo(
    () =>
      matchDuration >= 30 && matchDuration <= 120
        ? generateSlotsForDuration(matchDuration, slotIntervalMinutes)
        : [],
    [matchDuration, slotIntervalMinutes],
  );

  // Single-week edit mode: days come from the hydrated dates (initialSlots),
  // not from the range selector.
  const singleWeekDays = useMemo(() => {
    if (!singleWeekMode) return [];
    return Object.keys(initialSlots ?? {}).sort();
  }, [singleWeekMode, initialSlots]);

  // Fixed 7-day span starting at weekStartDate (LONG: first-round week).
  const weekStartDays = useMemo(() => {
    if (!weekStartDate) return [];
    const end = new Date(weekStartDate);
    end.setDate(end.getDate() + 6);
    return getDatesInRange(weekStartDate, end);
  }, [weekStartDate]);

  const daysInRange = useMemo(() => {
    if (weekStartDate) return weekStartDays;
    if (singleWeekMode) return singleWeekDays;
    if (!dateRange?.from || !dateRange?.to) return [];
    const from = dateRange.from;
    const to = dateRange.to;
    if (to < from) return [];
    return getDatesInRange(from, to);
  }, [weekStartDate, weekStartDays, singleWeekMode, singleWeekDays, dateRange]);

  const knockoutStart = useMemo(
    () =>
      allowKnockoutStart
        ? resolveKnockoutStart(knockoutMode, knockoutSlot, timeSlotsByDate, daysInRange)
        : null,
    [allowKnockoutStart, knockoutMode, knockoutSlot, timeSlotsByDate, daysInRange],
  );

  const knockoutDays = useMemo(
    () =>
      allowKnockoutStart
        ? daysInRange
            .map((date) => ({ date, startTimes: startTimesWithCourts(timeSlotsByDate, date) }))
            .filter((day) => day.startTimes.length > 0)
        : [],
    [allowKnockoutStart, daysInRange, timeSlotsByDate],
  );
  // Without a cut the bracket takes the whole last day, except when a single day is shared by both phases.
  const finalDay =
    knockoutMode !== 'GROUPS_ONLY' && knockoutDays.length > 1
      ? knockoutDays[knockoutDays.length - 1].date
      : null;

  const handleToggleKnockoutStart = useCallback(
    (date: string, startTime: string) => {
      const isCurrent = knockoutStart?.date === date && knockoutStart.startTime === startTime;
      setKnockoutSlot(isCurrent ? null : { date, startTime });
    },
    [knockoutStart],
  );

  // Prune per-day UI state when date range changes (no mutation, keeps overlap).
  useEffect(() => {
    if (daysInRange.length === 0) {
      setCheckedSlotsByDate({});
      setMassSelectionEnabledByDate({});
      return;
    }

    const daySet = new Set(daysInRange);

    setCheckedSlotsByDate((prev) => {
      const next: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (daySet.has(k)) next[k] = v;
      }
      return next;
    });

    setMassSelectionEnabledByDate((prev) => {
      const next: Record<string, true> = {};
      for (const k of Object.keys(prev)) {
        if (daySet.has(k)) next[k] = true;
      }
      return next;
    });
  }, [daysInRange]);

  const totals = useMemo(() => {
    let activeSlots = 0;
    let courts = 0;
    for (const byTime of Object.values(timeSlotsByDate)) {
      for (const c of Object.values(byTime)) {
        if (c > 0) {
          activeSlots += 1;
          courts += c;
        }
      }
    }
    return { days: daysInRange.length, activeSlots, courts };
  }, [timeSlotsByDate, daysInRange.length]);

  // In fixed-week-start mode, drop grid slots outside the new 7-day window when
  // the start date changes (guarded by weekStartDate, so SHORT is unaffected).
  useEffect(() => {
    if (!weekStartDate) return;
    const daySet = new Set(weekStartDays);
    setTimeSlotsByDate((prev) => {
      const next: TimeSlotsByDate = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (daySet.has(k)) next[k] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [weekStartDate, weekStartDays]);

  // Emit the current grid to the parent (LONG reads the selection without the
  // internal submit). Ref keeps the callback identity out of the deps to avoid loops.
  const onSlotsChangeRef = useRef(onSlotsChange);
  useEffect(() => {
    onSlotsChangeRef.current = onSlotsChange;
  }, [onSlotsChange]);
  useEffect(() => {
    if (!onSlotsChangeRef.current) return;
    onSlotsChangeRef.current(buildScheduleEntries(timeSlotsByDate, availableSlots));
  }, [timeSlotsByDate, availableSlots]);

  const handleSlotChange = useCallback((dateStr: string, startTime: string, courtCount: number) => {
    const checked = checkedSlotsByDate[dateStr];
    const isInGroup = checked?.includes(startTime);

    setTimeSlotsByDate((prev) => {
      const day = { ...(prev[dateStr] ?? {}) };
      if (isInGroup && checked && checked.length > 0) {
        checked.forEach((st) => {
          day[st] = courtCount;
        });
      } else {
        day[startTime] = courtCount;
      }
      return { ...prev, [dateStr]: day };
    });
  }, [checkedSlotsByDate]);

  const handleToggleCheck = useCallback((dateStr: string, startTime: string) => {
    setCheckedSlotsByDate((prev) => {
      const list = prev[dateStr] ?? [];
      const next = list.includes(startTime)
        ? list.filter((st) => st !== startTime)
        : [...list, startTime].sort();
      if (next.length === 0) {
        const rest = { ...prev };
        delete rest[dateStr];
        return rest;
      }
      return { ...prev, [dateStr]: next };
    });
  }, []);

  const handleUncheckAll = useCallback(() => {
    setCheckedSlotsByDate({});
    setMassSelectionEnabledByDate({});
  }, []);

  const handleEnableMassSelectionDay = useCallback((dateStr: string) => {
    setMassSelectionEnabledByDate((prev) => ({ ...prev, [dateStr]: true }));
  }, []);

  const handleDisableMassSelectionDay = useCallback((dateStr: string) => {
    setCheckedSlotsByDate((prev) => {
      const rest = { ...prev };
      delete rest[dateStr];
      return rest;
    });
    setMassSelectionEnabledByDate((prev) => {
      const rest = { ...prev };
      delete rest[dateStr];
      return rest;
    });
  }, []);

  const handleCheckRange = useCallback((dateStr: string, startTimes: string[]) => {
    setCheckedSlotsByDate((prev) => ({
      ...prev,
      [dateStr]: [...new Set([...(prev[dateStr] ?? []), ...startTimes])].sort(),
    }));
  }, []);

  const handleCopy = useCallback((dateStr: string) => {
    const byTime = timeSlotsByDate[dateStr];
    if (!byTime) return;
    const withCourts = { ...byTime };
    setCopiedSlots(withCourts);
  }, [timeSlotsByDate]);

  const handlePaste = useCallback((dateStr: string) => {
    if (!copiedSlots) return;
    setTimeSlotsByDate((prev) => ({
      ...prev,
      [dateStr]: { ...copiedSlots },
    }));
  }, [copiedSlots]);

  const handleResetDay = useCallback((dateStr: string) => {
    setTimeSlotsByDate((prev) => {
      const next = { ...prev };
      delete next[dateStr];
      return next;
    });
    setCheckedSlotsByDate((prev) => {
      const rest = { ...prev };
      delete rest[dateStr];
      return rest;
    });
    setResetDayTarget(null);
  }, []);

  const handleResetAll = useCallback(() => {
    setTimeSlotsByDate({});
    setCheckedSlotsByDate({});
    setDateRange(undefined);
    setCopiedSlots(null);
    setResetAllOpen(false);
  }, []);

  const handleIntervalChange = useCallback(
    (newInterval: number) => {
      if (newInterval === slotIntervalMinutes) return;
      if (totals.activeSlots > 0) {
        setPendingIntervalChange(newInterval);
      } else {
        setSlotIntervalMinutes(newInterval);
      }
    },
    [slotIntervalMinutes, totals.activeSlots],
  );

  const handleConfirmIntervalChange = useCallback(() => {
    if (pendingIntervalChange !== null) {
      setSlotIntervalMinutes(pendingIntervalChange);
      setTimeSlotsByDate({});
      setCheckedSlotsByDate({});
      setMassSelectionEnabledByDate({});
      setCopiedSlots(null);
      setPendingIntervalChange(null);
    }
  }, [pendingIntervalChange]);

  const handleSubmit = async () => {
    if (!onSubmitRequest) return;
    if (!nameValid || totals.activeSlots === 0 || availableSlots.length === 0) return;
    const schedule = buildScheduleEntries(timeSlotsByDate, availableSlots);
    if (schedule.length === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmitRequest({
        name: name.trim(),
        schedule,
        ...(knockoutStart
          ? {
              knockoutStartDate: knockoutStart.date,
              knockoutStartTime: knockoutStart.startTime,
            }
          : {}),
      });
      onClose?.();
    } catch {
      // Parent / handleGlobalError already showed toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const maxCourts =
    sportUid != null && String(sportUid).trim()
      ? getCourtsCountForSport(club, sportUid)
      : club.courts?.length ?? 4;
  const canSubmit = nameValid && totals.activeSlots > 0 && availableSlots.length > 0;

  return (
    <div className="space-y-6">
      {/* hideSubmit: the parent (wizard) drives continuation, so no name/submit here */}
      {!hideSubmit && (
        <BlockNameInput
          value={name}
          onChange={setName}
          error={nameError}
        />
      )}

      {/* Hide the date selector for single-week edit and fixed-week-start (LONG) modes */}
      {!singleWeekMode && !weekStartDate && (
        <ScheduleDatesSelector
          mode={dateSelectionMode}
          value={dateRange}
          onSelect={setDateRange}
          minDate={minDate}
        />
      )}

      {totals.days > 0 && (
        <div className="flex flex-wrap gap-4 rounded-lg border bg-muted/30 p-3 text-sm">
          <span>
            <strong>{totals.days}</strong> día{totals.days !== 1 ? 's' : ''}
          </span>
          <span>
            <strong>{totals.activeSlots}</strong> horario{totals.activeSlots !== 1 ? 's' : ''} activo{totals.activeSlots !== 1 ? 's' : ''}
          </span>
          <span>
            <strong>{totals.courts}</strong> cancha{totals.courts !== 1 ? 's' : ''} configurada{totals.courts !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {availableSlots.length === 0 && (name.length > 0 || daysInRange.length > 0) && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          La duración de partido debe estar entre 30 y 120 minutos para generar horarios.
        </p>
      )}

      {daysInRange.length > 0 && availableSlots.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Horarios por día</h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Cada</span>
                <Select
                  value={String(slotIntervalMinutes)}
                  onValueChange={(v) => handleIntervalChange(Number(v))}
                >
                  <SelectTrigger className="w-[5.5rem] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLOT_INTERVAL_OPTIONS.map((interval) => (
                      <SelectItem key={interval} value={String(interval)}>
                        {interval} min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUncheckAll}
                disabled={
                  Object.keys(checkedSlotsByDate).length === 0 &&
                  Object.keys(massSelectionEnabledByDate).length === 0
                }
                title="Quitar todos los checks de sincronización"
              >
                <Square className="mr-1 h-4 w-4" />
                Desmarcar todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetAllOpen(true)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Resetear todo
              </Button>
            </div>
          </div>
          {allowKnockoutStart && (
            <KnockoutStartPanel
              mode={knockoutMode}
              onModeChange={setKnockoutMode}
              knockoutStart={knockoutStart}
              onSlotChange={setKnockoutSlot}
              days={knockoutDays}
              finalDay={finalDay}
            />
          )}
          <div className="grid gap-4 sm:grid-cols-1">
            {daysInRange.map((dateStr, i) => (
              <DayScheduleCard
                key={dateStr}
                dateStr={dateStr}
                slots={availableSlots}
                timeSlotsByDate={timeSlotsByDate}
                maxCourts={maxCourts}
                onSlotChange={handleSlotChange}
                checkedSlotsForDay={checkedSlotsByDate[dateStr] ?? []}
                massSelectionEnabled={Boolean(massSelectionEnabledByDate[dateStr])}
                onEnableMassSelection={() => handleEnableMassSelectionDay(dateStr)}
                onDisableMassSelection={() => handleDisableMassSelectionDay(dateStr)}
                onToggleCheck={massSelectionEnabledByDate[dateStr] ? handleToggleCheck : undefined}
                onCheckRange={massSelectionEnabledByDate[dateStr] ? handleCheckRange : undefined}
                onCopy={handleCopy}
                onResetDay={(d) => setResetDayTarget(d)}
                onPaste={handlePaste}
                hasCopiedSlots={copiedSlots !== null}
                index={i}
                knockoutStart={knockoutStart}
                finalDay={finalDay}
                onToggleKnockoutStart={
                  allowKnockoutStart && knockoutMode === 'FROM_SLOT'
                    ? handleToggleKnockoutStart
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {!hideSubmit && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {submittingLabel}
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                {submitLabel}
              </>
            )}
          </Button>
        </div>
      )}

      <ConfirmationModal
        isOpen={resetDayTarget !== null}
        onClose={() => setResetDayTarget(null)}
        onConfirm={() => resetDayTarget && handleResetDay(resetDayTarget)}
        title="Resetear día"
        description="¿Borrar la configuración de horarios de este día?"
        confirmText="Resetear"
      />

      <ConfirmationModal
        isOpen={resetAllOpen}
        onClose={() => setResetAllOpen(false)}
        onConfirm={handleResetAll}
        title="Resetear todo el bloque"
        description="Se borrarán todos los días y horarios configurados. Esta acción no se puede deshacer."
        confirmText="Resetear todo"
      />

      <ConfirmationModal
        isOpen={pendingIntervalChange !== null}
        onClose={() => setPendingIntervalChange(null)}
        onConfirm={handleConfirmIntervalChange}
        title="Cambiar visualización de horarios"
        description="Si cambiás el intervalo, se resetearán todos los horarios seleccionados. ¿Continuar?"
        confirmText="Sí, cambiar y resetear"
        cancelText="Cancelar"
      />
    </div>
  );
}
